import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { CheckCircle2, Eraser } from "lucide-react";
import { useBrand } from "../context/BrandContext";

export function ApproveModal({ open, onOpenChange, postTitle, onApprove, submitting }) {
  const brand = useBrand();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const now = new Date();

  useEffect(() => {
    if (open) setTimeout(() => clearCanvas(), 60);
  }, [open]);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const c = canvasRef.current;
    if (!c) return;
    c.setPointerCapture(e.pointerId);
    const ctx = c.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    drawing.current = true;
  };

  const moveDraw = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#1C2733";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const endDraw = () => {
    drawing.current = false;
  };

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const emailValid = email.trim().includes("@") && email.trim().includes(".");

  const submit = () => {
    const signature_png = canvasRef.current.toDataURL("image/png").split(",")[1];
    onApprove({ signature_name: name.trim(), reviewer_email: email.trim(), signature_png });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-xl max-h-[90vh] overflow-y-auto" data-testid="approve-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black tracking-tighter flex items-center gap-2">
            <CheckCircle2 className="h-6 w-6 text-[var(--brand-primary)]" /> Approve this article
          </DialogTitle>
          <DialogDescription className="text-neutral-500 pt-1">
            You are approving <span className="font-semibold text-neutral-700">"{postTitle}"</span> for
            publication. The {brand.display_name} team will be notified to schedule it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="signature" className="font-mono-accent text-xs uppercase tracking-[0.2em] text-neutral-500">
              Full Name
            </Label>
            <Input
              id="signature"
              data-testid="signature-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Jane Smith"
              className="h-11 rounded-lg"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reviewer-email" className="font-mono-accent text-xs uppercase tracking-[0.2em] text-neutral-500">
              Confirm Your Email
            </Label>
            <Input
              id="reviewer-email"
              type="email"
              data-testid="reviewer-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="h-11 rounded-lg"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="font-mono-accent text-xs uppercase tracking-[0.2em] text-neutral-500">
                Draw Your Signature
              </Label>
              <button
                type="button"
                data-testid="signature-clear-button"
                onClick={clearCanvas}
                className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-800 transition-colors"
              >
                <Eraser className="h-3.5 w-3.5" /> Clear
              </button>
            </div>
            <canvas
              ref={canvasRef}
              width={520}
              height={150}
              data-testid="signature-canvas"
              onPointerDown={startDraw}
              onPointerMove={moveDraw}
              onPointerUp={endDraw}
              onPointerLeave={endDraw}
              className="w-full h-[150px] bg-white border border-dashed border-neutral-300 rounded-lg touch-none cursor-crosshair"
            />
            {!hasDrawn && (
              <p className="text-xs text-neutral-400">Sign above with your mouse or finger.</p>
            )}
          </div>

          <div className="bg-neutral-50 border border-black/[0.06] rounded-lg px-4 py-3">
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-1">
              Timestamp (recorded on approval)
            </p>
            <p className="font-mono-accent text-sm text-neutral-700" data-testid="approve-timestamp">
              {now.toLocaleString()}
            </p>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="confirm"
              data-testid="approve-confirm-checkbox"
              checked={confirmed}
              onCheckedChange={(v) => setConfirmed(v === true)}
              className="mt-0.5"
            />
            <label htmlFor="confirm" className="text-sm text-neutral-600 leading-relaxed cursor-pointer">
              I have reviewed this article and approve it for publication. I treat this signature as
              legally binding.
            </label>
          </div>

          <Button
            data-testid="approve-submit-button"
            disabled={!name.trim() || !emailValid || !hasDrawn || !confirmed || submitting}
            onClick={submit}
            className="w-full h-11 rounded-full bg-green-600 hover:bg-green-700 active:scale-95 transition-colors text-white font-semibold"
          >
            {submitting ? "Submitting..." : "Sign & Approve"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
